/**
 * ConfirmDialog + useConfirm — стилізована заміна window.confirm для важливих
 * дій (зміна статусу, видалення, відвантаження). Проміс-базований API:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Видалити?", danger: true })) { ... }
 *
 * Провайдер монтується один раз у App.tsx; діалог рендериться через <Modal>.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Modal } from "./Modal";
import { AuroraButton } from "./Glass";
import { Warning } from "./icons";

export interface ConfirmOptions {
  title: ReactNode;
  /** Додатковий пояснювальний текст під заголовком. */
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Червоний акцент + іконка попередження — для незворотних дій. */
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}

/** Те саме, але без провайдера повертає `null`, а не падає. Для `Modal`:
 *  він і сам малює діалог підтвердження, і буває поза провайдером. */
export function useOptionalConfirm(): ConfirmFn | null {
  return useContext(ConfirmContext);
}

interface Pending extends ConfirmOptions {
  resolve: (v: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const location = useLocation();

  // Провайдер живе НАД маршрутами, тож відкрите вікно переживає перехід на
  // іншу сторінку: екран уже інший, а питання висить — і відповідь «так»
  // виконує запис від імені знищеного компонента (напр. правку годин після
  // того, як користувача викинуло на /login). Тому при зміні маршруту
  // питання скасовуємо: не відповіли — значить, не підтвердили.
  useEffect(() => {
    setPending((prev) => {
      prev?.resolve(false);
      return null;
    });
  }, [location.pathname]);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      // Якщо вже є відкритий діалог — скасовуємо його (resolve(false)), щоб
      // його await не завис назавжди, і показуємо новий.
      setPending((prev) => {
        prev?.resolve(false);
        return { ...opts, resolve };
      });
    });
  }, []);

  const close = (v: boolean) => {
    pending?.resolve(v);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <Modal open onClose={() => close(false)} size="sm" labelledBy="confirm-title">
          <div className="flex items-start gap-3">
            {pending.danger && (
              <span className="flex-none text-aurora-danger mt-0.5">
                <Warning size={24} />
              </span>
            )}
            <div className="min-w-0">
              <h3 id="confirm-title" className="text-base font-bold text-aurora-ink m-0">
                {pending.title}
              </h3>
              {pending.body && (
                <p className="text-sm text-aurora-muted mt-1.5 whitespace-pre-line">
                  {pending.body}
                </p>
              )}
            </div>
          </div>
          <div className="flex gap-2 justify-end mt-5 sticky bottom-0 bg-white pt-3 -mx-5 px-5 -mb-5 pb-5">
            <AuroraButton variant="ghost" onClick={() => close(false)}>
              {pending.cancelLabel ?? "Скасувати"}
            </AuroraButton>
            <AuroraButton
              variant={pending.danger ? "danger" : "primary"}
              onClick={() => close(true)}
            >
              {pending.confirmLabel ?? "Підтвердити"}
            </AuroraButton>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}
